<template>
    <AdminPageInner v-if="course.id" :breadcrumbs="breadcrumbs">
        <template #heading>
            {{ course.title }}
        </template>
        <template #links>
            <ul class="flex items-center space-x-4">
                <li>
                    <router-link
                        v-if="course.user_can.update"
                        :to="{name: 'admin.courses.edit', params: {courseId: course.id}}">
                        Edit
                    </router-link>
                </li>
                <li>
                    <button
                        v-if="course.user_can.delete"
                        class="text-red-600"
                        @click.prevent="confirmDelete">
                        Delete
                    </button>
                </li>
            </ul>
        </template>

        <div v-if="course.id" class="space-y-6">
            <div class="row gutters-3 lg:flex-row-reverse">
                <div class="col-full lg:col-auto lg:min-w-96">
                    <InfoCard v-if="course.id">
                        <InfoCardItem title="Course Type" :content="courseType"/>
                        <InfoCardItem title="Pathway" :content="course.pathway.name"/>
                        <InfoCardItem title="Credits" :content="course.credits"/>
                        <InfoCardItem title="Trainers">
                            <ul v-if="course.trainers.length" class="space-y-4">
                                <li v-for="trainer in course.trainers">
                                    <router-link
                                        v-if="trainer.user_can.view"
                                        :to="{name: 'admin.users.show', params: {userId: trainer.id }}"
                                        targte="_blank">
                                        {{ trainer.name }}
                                    </router-link>
                                    <span v-else>{{ trainer.name }}</span>
                                </li>
                            </ul>
                        </InfoCardItem>
                        <InfoCardItem title="Moderators">
                            <ul v-if="course.moderators.length" class="space-y-4">
                                <li v-for="moderator in course.moderators">
                                    <router-link
                                        v-if="moderator.user_can.view"
                                        :to="{name: 'admin.users.show', params: {userId: moderator.id }}"
                                        targte="_blank">
                                        {{ moderator.name }}
                                    </router-link>
                                    <span v-else>{{ moderator.name }}</span>
                                </li>
                            </ul>
                        </InfoCardItem>
                    </InfoCard>
                </div>
                <div class="col-full mt-6 space-y-6 lg:col lg:mt-0">
                    <Card>
                        <template #header>
                            <h3 class="text-lg leading-6 font-medium text-gray-900">
                                Course Details
                            </h3>
                        </template>
                        <dl class="space-y-6">
                            <div>
                                <dt class="leading-5 font-medium text-gray-500">
                                    Summary
                                </dt>
                                <dd class="mt-4" v-html="course.summary"/>
                            </div>
                            <div>
                                <dt class="leading-5 font-medium text-gray-500">
                                    Description
                                </dt>
                                <dd class="mt-4" v-html="course.description"/>
                            </div>
                            <div v-if="course.multipart">
                                <dt class="leading-5 font-medium text-gray-500">
                                    Modules
                                </dt>
                                <dd class="prose max-w-full">
                                    <ol class="space-y-1">
                                        <li v-for="module in modules">
                                            {{ module.title }}
                                        </li>
                                    </ol>
                                </dd>
                            </div>
                            <div>
                                <dt class="leading-5 font-medium text-gray-500">
                                    Main Image
                                </dt>
                                <dd>
                                    <img
                                        v-if="course.main_image"
                                        :src="course.main_image.url"
                                        alt=""
                                        class="w-full max-w-56 mb-2"/>
                                    <DownloadShowCard
                                        v-if="course.main_image"
                                        :filename="course.main_image.original_file_name"
                                        :url="course.main_image.url"/>
                                    <span v-else>-</span>
                                </dd>
                            </div>
                            <div>
                                <dt class="leading-5 font-medium text-gray-500">
                                    Thumbnail Image
                                </dt>
                                <dd>
                                    <img
                                        v-if="course.thumbnail_image"
                                        :src="course.thumbnail_image.url"
                                        alt=""
                                        class="w-full max-w-56 mb-2"/>
                                    <DownloadShowCard
                                        v-if="course.thumbnail_image"
                                        :filename="course.thumbnail_image.original_file_name"
                                        :url="course.thumbnail_image.url"/>
                                    <span v-else>-</span>
                                </dd>
                            </div>
                        </dl>
                    </Card>
                </div>
            </div>
        </div>
        <div class="bg-white shadow overflow-hidden sm:rounded-lg mt-6">
            <div class="sm:flex justify-between px-4 py-5 border-b border-gray-200 sm:px-6">
                <h3 class="text-lg leading-6 font-medium text-gray-900">
                    Upcoming Sessions
                </h3>
                <router-link
                    :to="{name: 'admin.courses.session-groups.create', params: {courseId: course.id}}"
                    class="inline-block mt-4 sm:mt-0">
                    Create
                </router-link>
            </div>

            <div class="p-6 bg-cool-gray-100">
                <CourseSessionGroupsDataComponent
                    class="w-full"
                    :data="sessionGroups.data"
                    :disableSort="true"
                    @remove:sessionGroup="onRemoveSessionGroup"/>
            </div>

            <div class="px-4 py-5 border-t border-gray-200 sm:px-6 flex justify-between items-center">
                <PaginationShowing v-if="sessionGroups.meta.total" :meta="sessionGroups.meta"/>
                <router-link :to="{name: 'admin.courses.session-groups.index', params: {courseId: course.id}}">
                    View all sessions
                </router-link>
            </div>
        </div>

        <div v-if="assignments.data" class="bg-white shadow overflow-hidden sm:rounded-lg mt-6">
            <div class="sm:flex justify-between px-4 py-5 border-b border-gray-200 sm:px-6">
                <h3 class="text-lg leading-6 font-medium text-gray-900">
                    Assignments
                </h3>
            </div>

            <div class="p-6 bg-cool-gray-100">
                <AssignmentDataComponent
                    class="w-full"
                    :data="assignments.data"
                    :disableSort="true"/>
            </div>

            <div class="px-4 py-5 border-t border-gray-200 sm:px-6 flex justify-between items-center">
                <PaginationShowing v-if="assignments.meta.total" :meta="assignments.meta"/>
                <router-link :to="{name: 'admin.assignments.index', params: {courseId: course.id}}">
                    View all assignments
                </router-link>
            </div>
        </div>

        <div v-if="certificates.data" class="bg-white shadow overflow-hidden sm:rounded-lg mt-6">
            <div class="sm:flex justify-between px-4 py-5 border-b border-gray-200 sm:px-6">
                <h3 class="text-lg leading-6 font-medium text-gray-900">
                    Certificates
                </h3>
            </div>

            <div class="p-6 bg-cool-gray-100">
                <AssignmentDataComponent
                    class="w-full"
                    :data="certificates.data"
                    :isCertificate="true"
                    :disableSort="true"/>
            </div>

            <div class="px-4 py-5 border-t border-gray-200 sm:px-6 flex justify-between items-center">
                <PaginationShowing v-if="certificates.meta.total" :meta="certificates.meta"/>
                <router-link :to="{name: 'admin.certificates.index', params: {courseId: course.id}}">
                    View all certificates
                </router-link>
            </div>
        </div>

    </AdminPageInner>
</template>

<script>

import axios from "axios";
import {getCourseType} from "@/Lib/Helpers";
import AdminPageInner from "@/Components/Layout/AdminPageInner";
import Card from "@/Components/Card";
import {InfoCard, InfoCardItem} from "@/Components/InfoCard";
import CalendarFormat from "@/Components/CalendarFormat";
import DownloadShowCard from "@/Components/DownloadShowCard";
import {DataComponent, DataField} from "@/Components/DataComponent";
import PaginationShowing from "@/Components/PaginationShowing";
import {sortBy} from "lodash";
import CourseSessionGroupsDataComponent from "@/Pages/Admin/CourseSessionGroups/Components/DataComponent"
import AssignmentDataComponent from "@/Pages/Admin/Assignment/Components/DataComponent"
import dayjs from "dayjs";

export default {

    name: "CourseShowPage",

    data() {
        return {
            course: {},
            sessionGroups: {},
            assignments: {},
            certificates: {}
        }
    },

    components: {
        AdminPageInner,
        Card,
        InfoCard,
        InfoCardItem,
        CalendarFormat,
        DownloadShowCard,
        DataComponent,
        DataField,
        PaginationShowing,
        CourseSessionGroupsDataComponent,
        AssignmentDataComponent
    },

    methods: {
        setData(data) {
            this.course = data.course;
            this.sessionGroups = data.sessionGroups;
            this.assignments = data.assignments;
            this.certificates = data.certificates;
        },
        confirmDelete() {
            if (window.confirm('Are you sure you want to delete this Course?')) {
                axios.delete(route('api.courses.destroy', this.course))
                    .then(() => {
                        this.$notify.success('Course deleted');
                        this.$router.push({name: 'admin.courses.index'});
                    })
                    .catch(console.error);
            }
        },
        onRemoveSessionGroup() {
            this.$router.push({
                name: this.$route.name,
                params: this.$route.params,
                query: _.assign({}, this.$route.query, {t: dayjs().unix()})
            });
        },
    },

    computed: {
        breadcrumbs() {
            return [
                {label: 'Admin', to: {name: 'admin'}},
                {label: 'Courses', to: {name: 'admin.courses.index'}},
                {label: `${this.course.title || 'Course'}`, to: {name: this.$route.name, params: this.$route.params}},
            ];
        },
        modules() {

            return this.course.modules && this.course.multipart
                ? sortBy(this.course.modules, ['position', 'id'])
                : [];
        },
        courseType() {
            return getCourseType(this.course.type)
        },
    },

    async beforeRouteEnter(to, from, next) {
        const data = await fetchData(to);
        next(vm => vm.setData(data));
    },

    async beforeRouteUpdate(to, from) {
        const data = await fetchData(to);
        this.setData(data);
    },
};

const fetchData = async (to) => {
    const data = await Promise.all([
        axios.get(route('api.courses.show', to.params.courseId), {params: to.query}),
        axios.get(route('api.course-session-groups.index'), {
            params: {
                course_id: to.params.courseId,
                upcoming: true,
                per_page: 5
            }
        }),
        axios.get(route('api.course.assignments'), {
            params: {
                course_id: to.params.courseId,
                per_page: 5,
                type: 1
            }
        }),
        axios.get(route('api.course.assignments'), {
            params: {
                course_id: to.params.courseId,
                per_page: 5,
                type: 2
            }
        })
    ])

    return {
        course: data[0].data,
        sessionGroups: data[1].data,
        assignments: data[2].data,
        certificates: data[3].data
    };
}
</script>
